import { useMemo, useRef, type CSSProperties } from 'react';
import { prefersReducedMotion } from '@/lib/motion';
import { barOfBeat, beatInBar, beatPosition } from '@/core/analysis/grid';
import type { AnalysisResult } from '@/core/analysis/types';
import { chordName, LOW_CONFIDENCE, type DisplayOptions } from '@/core/theory/display';
import { useAnimationFrame } from '@/lib/useAnimationFrame';
import { useTimeIndex } from '@/lib/useTimeIndex';
import { getTransport } from '@/state/playerStore';
import styles from './ChordTimeline.module.css';

/** Width of one beat in px (design: Live.dc.html timeline mode). */
const BEAT_WIDTH = 96;

interface ChordTimelineProps {
  result: AnalysisResult;
  options: DisplayOptions;
  visualOffsetMs: number;
  onSeekBeat: (beat: number) => void;
  /** Flash the next chord this many beats ahead (F4.7); 0 = off. */
  warnBeats: number;
}

/**
 * One-line chord timeline (F4.2): chord blocks sized by beats scroll under a fixed playhead,
 * with a Now/Next strip above. Motion runs per frame on the DOM, not through React state.
 */
export function ChordTimeline({
  result,
  options,
  visualOffsetMs,
  onSeekBeat,
  warnBeats,
}: ChordTimelineProps) {
  const grid = result.beatGrid;
  const { segments } = result;
  const starts = useMemo(() => segments.map((s) => s.start), [segments]);
  const active = useTimeIndex(starts, visualOffsetMs);
  const trackRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  const totalBeats = grid.beats.length;
  const downbeats = useMemo(() => {
    const out: number[] = [];
    for (let b = grid.firstDownbeat; b < totalBeats; b += grid.beatsPerBar) out.push(b);
    return out;
  }, [grid, totalBeats]);

  const current = segments[active];
  const next = segments[active + 1];

  useAnimationFrame(() => {
    const t = getTransport();
    const track = trackRef.current;
    if (!t || !track) return;
    const exact = beatPosition(grid, t.getPosition(visualOffsetMs));
    const pos = prefersReducedMotion() ? Math.floor(exact) : exact;
    track.style.transform = `translateX(${(-pos * BEAT_WIDTH).toFixed(1)}px)`;

    const beat = Math.floor(exact);
    const seg = segments[active];
    const fill = track.querySelector<HTMLElement>('[data-active] [data-fill]');
    if (fill && seg) {
      const progress = Math.min(1, Math.max(0, (exact - seg.startBeat) / seg.beatCount));
      fill.style.width = `${(progress * 100).toFixed(1)}%`;
    }
    if (nextRef.current) {
      const left = next ? next.startBeat - beat : 0;
      nextRef.current.toggleAttribute(
        'data-warn',
        Boolean(next?.chord) && beat >= 0 && left > 0 && left <= warnBeats,
      );
    }
    if (countRef.current) {
      const left = next ? next.startBeat - beat : 0;
      countRef.current.textContent =
        next && beat >= 0 ? `in ${left} ${left === 1 ? 'beat' : 'beats'}` : '';
    }
    if (dotsRef.current)
      dotsRef.current.dataset.beat = beat < 0 ? '0' : String(beatInBar(grid, beat));
    if (barRef.current)
      barRef.current.textContent = beat < 0 ? '' : `Bar ${barOfBeat(grid, beat) || 'pickup'}`;
  });

  return (
    <section aria-label="Chord timeline" className={styles.section}>
      <div className={styles.strip}>
        <div className={styles.now}>
          <span className={styles.eyebrow}>Now</span>
          <span className={styles.nowName} aria-live="polite" data-testid="timeline-now">
            {current ? chordName(current.chord, options) : '–'}
          </span>
        </div>
        <div ref={nextRef} className={styles.next}>
          <span className={styles.eyebrow}>Next</span>
          <span className={styles.nextName}>{next ? chordName(next.chord, options) : '—'}</span>
          <span ref={countRef} className={styles.count} />
        </div>
        <span ref={barRef} className={styles.bar} />
        <div ref={dotsRef} className={styles.dots} data-beat="0" aria-hidden="true">
          {Array.from({ length: grid.beatsPerBar }, (_, i) => (
            <span key={i} className={styles.dot} data-n={i + 1} />
          ))}
        </div>
      </div>

      <div className={styles.hintRow}>
        <span>Click a chord to jump there</span>
      </div>

      <div className={styles.lane} data-testid="timeline-lane">
        <div className={styles.anchor}>
          <div
            ref={trackRef}
            className={styles.track}
            style={
              {
                width: totalBeats * BEAT_WIDTH,
                '--beat': `${BEAT_WIDTH}px`,
                '--bar': `${BEAT_WIDTH * grid.beatsPerBar}px`,
                '--bar-offset': `${grid.firstDownbeat * BEAT_WIDTH}px`,
              } as CSSProperties
            }
          >
            {downbeats.map((b) => (
              <span key={b} className={styles.barMark} style={{ left: b * BEAT_WIDTH + 4 }}>
                {barOfBeat(grid, b)}
              </span>
            ))}
            {segments.map((s, i) => {
              const label = chordName(s.chord, options);
              return (
                <button
                  key={s.startBeat}
                  type="button"
                  tabIndex={-1}
                  className={styles.block}
                  data-kind={s.chord ? 'chord' : 'no-chord'}
                  data-low={s.chord && s.confidence < LOW_CONFIDENCE ? true : undefined}
                  data-active={i === active || undefined}
                  data-past={(active >= 0 && i < active) || undefined}
                  style={{
                    left: s.startBeat * BEAT_WIDTH + 2,
                    width: s.beatCount * BEAT_WIDTH - 4,
                  }}
                  aria-label={`Jump to ${label}, bar ${barOfBeat(grid, s.startBeat) || 'pickup'}`}
                  onClick={() => {
                    onSeekBeat(s.startBeat);
                  }}
                >
                  <span className={styles.blockLabel}>{label}</span>
                  <span className={styles.fill} data-fill="" />
                </button>
              );
            })}
          </div>
        </div>
        <span className={styles.playhead} aria-hidden="true" />
        <span className={styles.playheadCap} aria-hidden="true" />
      </div>
    </section>
  );
}
